import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Card from '../../components/ui/Card';
import StatusTag from './StatusTag';
import type { TagTone } from './StatusTag';
import type { OutcomeDto } from './types';

// BP 27 §Outcome taxonomy — PLACEHOLDER outcomes, system-derived status from
// first-hand evidence only, shown beside the instructor's latest (authoritative
// when present) OutcomeAssessment. Never blends in a D2L result.
const SYSTEM_LABEL: Record<OutcomeDto['system_status'], string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  evidence_met: 'Evidence met',
};
const SYSTEM_TONE: Record<OutcomeDto['system_status'], TagTone> = {
  not_started: 'neutral',
  in_progress: 'accent',
  evidence_met: 'success',
};
const LEVEL_LABEL: Record<string, string> = {
  not_yet: 'Not yet',
  developing: 'Developing',
  demonstrated: 'Demonstrated',
};
const LEVEL_TONE: Record<string, TagTone> = {
  not_yet: 'danger',
  developing: 'accent',
  demonstrated: 'success',
};

function fmtDate(d: string | null | undefined): string {
  return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

export default function OutcomeGrid({ outcomes }: { outcomes: OutcomeDto[] }) {
  const [openCode, setOpenCode] = useState<string | null>(null);

  return (
    <Card className="overflow-hidden print:break-inside-avoid">
      <div className="border-b border-ws-border bg-ws-surface-2 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-ws-ink">Learning outcomes</h3>
        <p className="text-[11px] text-ws-ink-3">
          PLACEHOLDER taxonomy pending the program's own outcome list. "System" is computed from
          first-hand evidence only; the instructor's judgment, when given, is authoritative.
        </p>
      </div>

      {outcomes.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-ws-ink-3">
          No outcomes seeded yet.
        </p>
      ) : (
        <div className="divide-y divide-ws-border">
          {outcomes.map((o) => {
            const open = openCode === o.code;
            return (
              <div key={o.code}>
                <button
                  type="button"
                  onClick={() => setOpenCode(open ? null : o.code)}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-ws-surface-2"
                  aria-expanded={open}
                >
                  {open ? <ChevronDown size={14} className="text-ws-ink-3" /> : <ChevronRight size={14} className="text-ws-ink-3" />}
                  <span className="w-20 shrink-0 font-mono text-xs text-ws-ink-3">{o.code}</span>
                  <span className="flex-1 truncate text-ws-ink">{o.text}</span>
                  <span className="shrink-0">
                    <StatusTag label={SYSTEM_LABEL[o.system_status]} tone={SYSTEM_TONE[o.system_status]} size="sm" />
                  </span>
                  {o.assessment && (
                    <span className="shrink-0">
                      <StatusTag
                        label={LEVEL_LABEL[o.assessment.level] || o.assessment.level}
                        tone={LEVEL_TONE[o.assessment.level] || 'neutral'}
                        size="sm"
                      />
                    </span>
                  )}
                </button>
                {open && (
                  <div className="space-y-2 border-t border-ws-border bg-ws-surface-2/50 px-4 py-3 text-xs">
                    {o.bloom && <p className="text-ws-ink-3">Bloom level: {o.bloom}</p>}
                    {o.assessment ? (
                      <p className="text-ws-ink-2">
                        Instructor: <strong>{LEVEL_LABEL[o.assessment.level] || o.assessment.level}</strong>
                        {' — '}{o.assessment.evidence}
                        <span className="ml-1 text-ws-ink-4">
                          ({o.assessment.author?.name || 'instructor'}, {fmtDate(o.assessment.at)})
                        </span>
                      </p>
                    ) : (
                      <p className="text-ws-ink-3">No instructor assessment yet.</p>
                    )}
                    {o.history.length > 1 && (
                      <div className="space-y-1 border-t border-ws-border pt-2">
                        <p className="font-medium text-ws-ink-3">History</p>
                        {o.history.map((h, i) => (
                          <p key={h.id || i} className={i === 0 ? 'text-ws-ink-2' : 'text-ws-ink-4 line-through decoration-ws-ink-4/60'}>
                            {LEVEL_LABEL[h.level] || h.level} — {h.evidence} ({h.author?.name || 'instructor'}, {fmtDate(h.at)})
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
